import {
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
	useSyncExternalStore,
	type ReactNode,
	type SyntheticEvent,
} from "react";
import { flushSync } from "react-dom";
import { useTranslations } from "use-intl";

import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";
import { pageSession } from "@platform/core/page-session";

type SessionChange = (
	operation: () => Promise<"reload" | "rejected">,
	destination?: string,
) => Promise<void>;
const SessionTransitionContext = createContext<SessionChange | null>(null);

export const sessionMutationOptions = { retry: 0, timeout: 20_000 } as const;

export function SessionTransitionProvider({ children }: { children: ReactNode }) {
	const t = useTranslations("auth.sessionTransition");
	const busy = useRef(false);
	const [phase, setPhase] = useState<"idle" | "pending" | "unconfirmed">("idle");
	const [destination, setDestination] = useState("/tenant");
	const stale = useSyncExternalStore(pageSession.subscribe, pageSession.isStale, () => false);
	const blocked = phase !== "idle" || stale;
	const changed = stale && phase === "idle";

	const changeSession = useCallback<SessionChange>(async (operation, target = "/tenant") => {
		if (busy.current || pageSession.isStale()) return;
		busy.current = true;
		// Session cookies can change before the mutation response reaches the page.
		flushSync(() => {
			setDestination(target);
			setPhase("pending");
		});
		try {
			if ((await operation()) === "rejected") {
				busy.current = false;
				setPhase("idle");
				return;
			}
			window.location.assign(target);
		} catch {
			setPhase("unconfirmed");
		}
	}, []);

	function blockInteraction(event: SyntheticEvent) {
		// React also routes portal events through this boundary.
		if (busy.current || pageSession.isStale()) {
			event.preventDefault();
			event.stopPropagation();
		}
	}

	return (
		<SessionTransitionContext value={changeSession}>
			<div
				hidden={blocked}
				inert={blocked}
				onClickCapture={blockInteraction}
				onSubmitCapture={blockInteraction}
				onKeyDownCapture={blockInteraction}
			>
				{children}
			</div>
			<AlertDialog open={blocked}>
				<AlertDialogContent
					dismissOnOverlayClick={false}
					onEscapeKeyDown={(event) => event.preventDefault()}
				>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t(
								changed
									? "changedTitle"
									: phase === "unconfirmed"
										? "unconfirmedTitle"
										: "pendingTitle",
							)}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t(changed ? "changed" : phase === "unconfirmed" ? "unconfirmed" : "pending")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					{(phase === "unconfirmed" || changed) && (
						<AlertDialogFooter>
							<Button onClick={() => window.location.assign(destination)}>{t("reload")}</Button>
						</AlertDialogFooter>
					)}
				</AlertDialogContent>
			</AlertDialog>
		</SessionTransitionContext>
	);
}

export function useSessionTransition() {
	const changeSession = useContext(SessionTransitionContext);
	if (!changeSession) throw new Error("SessionTransitionProvider is required");
	return changeSession;
}
